// Link a drawn row to its counterpart in the model, and open that element in 3D.
//
// The candidates come from the live commit, narrowed to the categories the
// row's system reads, so the list is short enough to recognise. Nothing is
// guessed: a person picks, because the register row and the model element
// are only "the same thing" in someone's judgement (owner, 2026-09-17: "an
// option to click the 2D element and map it to the 3D counterpart from the
// isometric view to the 3D view").
import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Skeleton, Table, Tag, Tooltip, message } from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { viewsApi } from "./viewModel";

export const openInModel = (projectId, uniqueId) => {
  window.open(`/split-search?projectId=${encodeURIComponent(projectId)}&uniqueId=${encodeURIComponent(uniqueId)}`, "_blank", "noopener");
};

// canAdd links an element, canDelete unlinks one (technicalDocs*, 2026-10-14).
export default function ModelLinkModal({ projectId, viewId, row, canAdd = false, canDelete = false, open, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);

  const load = useCallback((text, everything) => {
    if (!row) return;
    setState(null);
    const params = [text ? `q=${encodeURIComponent(text)}` : "", everything ? "all=1" : ""].filter(Boolean).join("&");
    viewsApi(projectId, `/${viewId}/rows/${encodeURIComponent(row.id)}/candidates${params ? `?${params}` : ""}`)
      .then(setState)
      .catch((e) => { setState({ candidates: [], linked: [], reason: e.message }); });
  }, [projectId, viewId, row]);
  useEffect(() => { if (open) { setQ(""); setPicked([]); setAll(false); load("", false); } }, [open, load]);

  const link = async () => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${viewId}/rows/${encodeURIComponent(row.id)}/links`, { method: "POST", body: { uniqueIds: picked } });
      setState((s) => ({ ...s, linked: out.linked }));
      setPicked([]);
      message.success(`${out.linked.length} model element${out.linked.length === 1 ? "" : "s"} linked to ${row.id}`);
      if (onChanged) onChanged();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  const unlink = async (uniqueId) => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${viewId}/rows/${encodeURIComponent(row.id)}/links/${encodeURIComponent(uniqueId)}`, { method: "DELETE" });
      setState((s) => ({ ...s, linked: out.linked }));
      if (onChanged) onChanged();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  if (!row) return null;
  const linked = (state && state.linked) || [];

  return (
    <Modal open={open} onCancel={onClose} width={900} footer={<Button onClick={onClose}>Close</Button>}
           title={`${row.id} · ${row.type}${row.size ? ` ${row.size}` : ""} — its counterpart in the model`}>
      {linked.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <b>Linked</b>
          <Table size="small" rowKey="uniqueId" pagination={false} dataSource={linked}
                 columns={[
                   { title: "Type", dataIndex: "type", render: (t, r) => <div><div>{t || r.name || "—"}</div><div className="g-tsub">{r.category}{r.family ? ` · ${r.family}` : ""}{r.level ? ` · ${r.level}` : ""}</div></div> },
                   { title: "UniqueId", dataIndex: "uniqueId", render: (v) => <code className="g-tsub">{String(v).slice(0, 18)}…</code> },
                   { title: "Linked by", dataIndex: "by", width: 140, render: (v, r) => <span className="g-tsub">{v}{r.at ? ` · ${String(r.at).slice(0, 10)}` : ""}</span> },
                   { title: "", width: 190, render: (_, r) => (
                     <span>
                       <Button size="small" type="link" onClick={() => openInModel(projectId, r.uniqueId)}>Open in 3D</Button>
                       {canDelete && <Tooltip title="Unlink"><Button size="small" type="text" icon={<DeleteOutlined />} loading={busy} onClick={() => unlink(r.uniqueId)} /></Tooltip>}
                     </span>
                   ) },
                 ]} />
        </div>
      )}

      {canAdd && (
        <>
          <div className="g-actions" style={{ marginBottom: 10 }}>
            <Input allowClear value={q} onChange={(e) => setQ(e.target.value)} onPressEnter={() => load(q, all)}
                   placeholder="Search the model by type, family, name or UniqueId" prefix={<SearchOutlined />} style={{ maxWidth: 420 }} />
            <Button onClick={() => load(q, all)}>Search</Button>
            <Button type={all ? "primary" : "default"} onClick={() => { setAll(!all); load(q, !all); }}>
              {all ? "Every category" : `Only ${row.system || "this system"}'s categories`}
            </Button>
            <Button type="primary" disabled={!picked.length} loading={busy} onClick={link}>Link{picked.length ? ` (${picked.length})` : ""}</Button>
          </div>

          {!state ? <Skeleton active paragraph={{ rows: 5 }} /> : state.candidates.length === 0 ? (
            <Empty description={state.reason || "Nothing in the model matches that in these categories. Try every category, or search by UniqueId."} />
          ) : (
            <Table size="small" rowKey="uniqueId" dataSource={state.candidates} pagination={{ pageSize: 10, size: "small" }}
                   rowSelection={{ selectedRowKeys: picked, onChange: setPicked }}
                   columns={[
                     { title: "Type", dataIndex: "typeName", render: (t, r) => t || r.name || "—" },
                     { title: "Family", dataIndex: "family" },
                     { title: "Category", dataIndex: "category", render: (c) => <Tag>{c}</Tag> },
                     { title: "Level", dataIndex: "level", width: 110 },
                     { title: "", width: 110, render: (_, r) => <Button size="small" type="link" onClick={() => openInModel(projectId, r.uniqueId)}>Open in 3D</Button> },
                   ]} />
          )}
          {state && state.categories && state.categories.length > 0 && !all && (
            <p className="g-tsub">Searching {state.categories.join(", ")}. Change what this system reads with Edit system mapping.</p>
          )}
        </>
      )}
    </Modal>
  );
}
